import React, { useEffect, useState } from "react";
import { 
  Briefcase, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Upload,
  X,
  Save,
  Clock,
  MapPin,
  DollarSign,
  Users,
  Star
} from "react-feather";
import { 
  collection, 
  getDocs, 
  doc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from "firebase/firestore";
import { db, uploadImage } from "shared/utils/firebase";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./JobRoleManagement.module.css";

function JobRoleManagement() {
  const [jobRoles, setJobRoles] = useState([]);
  const [filteredJobRoles, setFilteredJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterExperience, setFilterExperience] = useState("all");
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);

  const [jobForm, setJobForm] = useState({
    title: "",
    company: "",
    logo: "",
    location: "",
    experience: "",
    salary: "",
    category: "vlsi",
    experienceLevel: "entry",
    type: "Full-time",
    deadline: "",
    description: "",
    requirements: [],
    benefits: [],
    rating: 0,
    isUrgent: false,
    isRemote: false
  });

  const categories = ["vlsi", "embedded", "iot", "verification", "physical", "software"];
  const experienceLevels = ["entry", "mid", "senior"];

  useEffect(() => {
    fetchJobRoles();
  }, []);

  useEffect(() => {
    filterJobRoles();
  }, [jobRoles, searchTerm, filterCategory, filterExperience]);

  const fetchJobRoles = async () => {
    try {
      setLoading(true);
      const jobsSnapshot = await getDocs(
        query(collection(db, "jobRoles"), orderBy("posted", "desc"))
      );
      const jobsData = jobsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        posted: doc.data().posted || doc.data().createdAt?.toDate()?.toLocaleDateString(),
        deadline: doc.data().deadline?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      setJobRoles(jobsData);
    } catch (error) {
      console.error("Error fetching job roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobRoles = () => {
    let filtered = jobRoles;

    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter(job => job.category === filterCategory);
    }

    if (filterExperience !== "all") {
      filtered = filtered.filter(job => job.experienceLevel === filterExperience);
    }

    setFilteredJobRoles(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setJobForm(prev => ({ ...prev, [field]: items }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    uploadImage(
      file,
      (progress) => {
        setLogoProgress(progress);
      },
      (url) => {
        setJobForm(prev => ({ ...prev, logo: url }));
        setUploadingLogo(false);
        setLogoProgress(0);
      },
      (error) => {
        console.error("Logo upload error:", error);
        setUploadingLogo(false);
        setLogoProgress(0);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        ...jobForm,
        deadline: jobForm.deadline ? new Date(jobForm.deadline) : null,
        posted: editingJob ? editingJob.posted : new Date().toLocaleDateString(),
        applicants: editingJob ? editingJob.applicants || [] : [],
        applicantCount: editingJob ? editingJob.applicantCount || 0 : 0,
        createdAt: editingJob ? editingJob.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingJob) {
        await updateDoc(doc(db, "jobRoles", editingJob.id), jobData);
        setJobRoles(jobRoles.map(job => 
          job.id === editingJob.id ? { ...job, ...jobData } : job
        ));
      } else {
        const docRef = await addDoc(collection(db, "jobRoles"), jobData);
        setJobRoles([{ id: docRef.id, ...jobData }, ...jobRoles]);
      }

      setShowJobModal(false);
      setEditingJob(null);
      resetForm();
    } catch (error) {
      console.error("Error saving job role:", error);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title || "",
      company: job.company || "",
      logo: job.logo || "",
      location: job.location || "",
      experience: job.experience || "",
      salary: job.salary || "",
      category: job.category || "vlsi",
      experienceLevel: job.experienceLevel || "entry",
      type: job.type || "Full-time",
      deadline: job.deadline ? job.deadline.toISOString().split('T')[0] : "",
      description: job.description || "",
      requirements: job.requirements || [],
      benefits: job.benefits || [],
      rating: job.rating || 0,
      isUrgent: job.isUrgent || false,
      isRemote: job.isRemote || false
    });
    setShowJobModal(true);
  };

  const handleDelete = async () => {
    if (!jobToDelete) return;

    try {
      await deleteDoc(doc(db, "jobRoles", jobToDelete.id));
      setJobRoles(jobRoles.filter(job => job.id !== jobToDelete.id));
      setShowDeleteModal(false);
      setJobToDelete(null);
    } catch (error) {
      console.error("Error deleting job role:", error);
    }
  };

  const resetForm = () => {
    setJobForm({
      title: "",
      company: "",
      logo: "",
      location: "",
      experience: "",
      salary: "",
      category: "vlsi",
      experienceLevel: "entry",
      type: "Full-time",
      deadline: "",
      description: "",
      requirements: [],
      benefits: [],
      rating: 0,
      isUrgent: false,
      isRemote: false
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'vlsi': 'bg-primary',
      'embedded': 'bg-success',
      'iot': 'bg-info',
      'verification': 'bg-warning',
      'physical': 'bg-danger',
      'software': 'bg-secondary'
    };
    return colors[category] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading job roles...</p>
      </div>
    );
  }

  return (
    <div className={styles.jobRoleManagement}>
      <PageHeader
        icon={Briefcase}
        title="Job & Career Role Management"
        subtitle="Manage VLSI & Embedded Systems job postings, career opportunities, and requirements"
        badge={`${filteredJobRoles.length} Job Roles`}
        actions={
          <button 
            className={styles.addBtn}
            onClick={() => {
              setEditingJob(null);
              resetForm();
              setShowJobModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Add Job Role
          </button>
        }
      />

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={filterExperience}
            onChange={(e) => setFilterExperience(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Levels</option>
            {experienceLevels.map(level => (
              <option key={level} value={level}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{jobRoles.length}</div>
          <div className={styles.statLabel}>Total Jobs</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {jobRoles.filter(j => j.isUrgent).length}
          </div>
          <div className={styles.statLabel}>Urgent</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {jobRoles.filter(j => j.isRemote).length}
          </div>
          <div className={styles.statLabel}>Remote</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {jobRoles.reduce((sum, j) => sum + (j.applicantCount || 0), 0)}
          </div>
          <div className={styles.statLabel}>Total Applicants</div>
        </div>
      </div>

      <div className={styles.jobList}>
        {filteredJobRoles.map((job) => (
          <div key={job.id} className={styles.jobCard}>
            <div className={styles.jobHeader}>
              <div className={styles.jobInfo}>
                <div className={styles.companyInfo}>
                  {job.logo && (
                    <img src={job.logo} alt={job.company} className={styles.companyLogo} />
                  )}
                  <div>
                    <h5 className={styles.jobTitle}>{job.title}</h5>
                    <p className={styles.companyName}>{job.company}</p>
                  </div>
                </div>
                <div className={styles.jobMeta}>
                  <span className={`badge ${getCategoryColor(job.category)}`}>
                    {job.category}
                  </span>
                  {job.isUrgent && (
                    <span className="badge bg-danger">Urgent</span>
                  )}
                  {job.isRemote && (
                    <span className="badge bg-info">Remote</span>
                  )}
                </div>
              </div>
              <div className={styles.jobActions}>
                <button
                  className={styles.actionBtn}
                  onClick={() => handleEdit(job)}
                  title="Edit Job"
                >
                  <Edit size={16} />
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                  onClick={() => {
                    setJobToDelete(job);
                    setShowDeleteModal(true);
                  }}
                  title="Delete Job"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className={styles.jobContent}>
              <p>{job.description}</p>
              <div className={styles.jobDetails}>
                <div className={styles.detailItem}>
                  <MapPin size={14} className="me-1" />
                  <span>{job.location}</span>
                </div>
                <div className={styles.detailItem}>
                  <Clock size={14} className="me-1" />
                  <span>{job.experience}</span>
                </div>
                <div className={styles.detailItem}>
                  <DollarSign size={14} className="me-1" />
                  <span>{job.salary}</span>
                </div>
                <div className={styles.detailItem}>
                  <Users size={14} className="me-1" />
                  <span>{job.applicantCount || 0} applicants</span>
                </div>
              </div>
              {job.requirements && job.requirements.length > 0 && (
                <div className={styles.requirementsContainer}>
                  <strong>Requirements:</strong>
                  <div className={styles.tagsContainer}>
                    {job.requirements.slice(0, 5).map((req, index) => (
                      <span key={index} className="badge bg-light text-dark me-1">
                        {req}
                      </span>
                    ))}
                    {job.requirements.length > 5 && (
                      <span className="badge bg-secondary">+{job.requirements.length - 5} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredJobRoles.length === 0 && (
        <div className={styles.emptyState}>
          <Briefcase size={48} className="text-muted mb-3" />
          <h5>No job roles found</h5>
          <p className="text-muted">
            {searchTerm || filterCategory !== "all" || filterExperience !== "all" 
              ? "Try adjusting your search or filters"
              : "No job roles have been created yet"
            }
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingJob(null);
              resetForm();
              setShowJobModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Create First Job Role
          </button>
        </div>
      )}

      {/* Job Form Modal */}
      {showJobModal && (
        <div className={styles.modalOverlay} onClick={() => setShowJobModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>{editingJob ? 'Edit Job Role' : 'Create New Job Role'}</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowJobModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Job Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={jobForm.title}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Company *</label>
                  <input
                    type="text"
                    name="company"
                    value={jobForm.company}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Category *</label>
                  <select
                    name="category"
                    value={jobForm.category}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Experience Level *</label>
                  <select
                    name="experienceLevel"
                    value={jobForm.experienceLevel}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {experienceLevels.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={jobForm.location}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Experience</label>
                  <input
                    type="text"
                    name="experience"
                    value={jobForm.experience}
                    onChange={handleInputChange}
                    placeholder="e.g., 3-5 years"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Salary</label>
                  <input
                    type="text"
                    name="salary"
                    value={jobForm.salary}
                    onChange={handleInputChange}
                    placeholder="e.g., ₹15-25 LPA"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Deadline</label>
                  <input
                    type="date"
                    name="deadline"
                    value={jobForm.deadline}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Description *</label>
                <textarea
                  name="description"
                  value={jobForm.description}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Requirements (comma-separated)</label>
                <input
                  type="text"
                  value={jobForm.requirements.join(', ')}
                  onChange={(e) => handleArrayChange('requirements', e.target.value)}
                  placeholder="e.g., VLSI Design, Verilog, RTL Design"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Benefits (comma-separated)</label>
                <input
                  type="text"
                  value={jobForm.benefits.join(', ')}
                  onChange={(e) => handleArrayChange('benefits', e.target.value)}
                  placeholder="e.g., Health Insurance, Stock Options"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Company Logo</label>
                <div className={styles.imageUpload}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className={styles.fileInput}
                    id="company-logo"
                  />
                  <label htmlFor="company-logo" className={styles.fileLabel}>
                    <Upload size={16} className="me-2" />
                    Choose Logo
                  </label>
                  {uploadingLogo && (
                    <div className={styles.uploadProgress}>
                      <div className="progress">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${logoProgress}%` }}
                        />
                      </div>
                      <small>{logoProgress.toFixed(1)}% uploaded</small>
                    </div>
                  )}
                  {jobForm.logo && (
                    <div className={styles.imagePreview}>
                      <img src={jobForm.logo} alt="Company logo" />
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isUrgent"
                    checked={jobForm.isUrgent}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span>Urgent Position</span>
                </label>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isRemote"
                    checked={jobForm.isRemote}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span>Remote Position</span>
                </label>
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowJobModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <Save size={16} className="me-2" />
                  {editingJob ? 'Update Job Role' : 'Create Job Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && jobToDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Confirm Deletion</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete the job role <strong>"{jobToDelete.title}"</strong>?
              </p>
              <p className="text-danger">
                This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete Job Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobRoleManagement;

